<template>
  <n-card
    class="adminDashboardCard"
    :bordered="false"
    title="Day Management"
    style="margin-bottom: 5px"
  >
    <p>
      Click the button below to start or end this day within your event. You are
      also able to add a project to this current ongoing round.
      <span class="italicSubtext">
        Note that once you end a day you cannot restart it.
      </span>
    </p>
    <div class="primaryButton startEndEventDayButton">
      <n-button @click="handleButton" :loading="isLoading">
        {{ buttonText }}
      </n-button>
      <span className="eventDayDurationText" :key="activeEvent.dayOngoing">
        Running for: <span>{{ getTime }}</span>
      </span>
    </div>
    <div class="secondaryButton" v-if="activeEvent.currentRound > 0">
      <n-button
        @click="showAddProjectModal = true"
        :loading="updateProjectsLoading"
      >
        Add Projects to Round {{ activeEvent.currentRound + 1 }}
      </n-button>
    </div>
  </n-card>
  <div class="primaryModal">
    <n-modal
      :class="[isMobileView() ? 'popoverModalMobile' : 'popoverModal']"
      v-model:show="showEndDayModal"
      preset="card"
      title="End Day"
    >
      <div v-if="lastDay"></div>
      <div v-else>
        <div className="popoverModalBody">
          <p>
            Choose to continue this current round onto the next day in your
            event.
          </p>
          <div class="primaryButton continueRoundButton">
            <n-button @click="handleChoice(true)">
              Continue Round To Next Day
            </n-button>
          </div>
        </div>
        <div className="popoverModalBody">
          <p>
            Alternatively, configure how many teams per discipline you would like
            to advance to the next round.
          </p>

          <!-- Mode Toggle -->
          <n-space vertical style="margin-bottom: 16px;">
            <n-radio-group v-model:value="advancementMode" name="advancementMode">
              <n-radio value="simple">Simple: Same for all disciplines</n-radio>
              <n-radio value="advanced">Advanced: Per-discipline configuration</n-radio>
            </n-radio-group>
          </n-space>

          <!-- Simple Mode Configuration -->
          <div v-if="isSimpleMode" style="margin-bottom: 16px;">
            <n-space vertical>
              <span>Number of projects to advance per discipline:</span>
              <n-input-number
                v-model:value="defaultTopCut"
                :min="0"
                :max="1000"
                clearable
                style="width: 200px;"
              />
            </n-space>
          </div>

          <!-- Advanced Mode Configuration -->
          <div v-else style="margin-bottom: 16px;">
            <n-space vertical>
              <span>Default number to advance:</span>
              <n-input-number
                v-model:value="defaultTopCut"
                :min="0"
                :max="1000"
                clearable
                style="width: 200px; margin-bottom: 12px;"
              />

              <n-divider style="margin: 12px 0;" />

              <span style="font-weight: 500;">Per-Discipline Overrides:</span>
              <div v-for="discipline in activeEvent.disciplines" :key="discipline" style="margin-bottom: 8px;">
                <n-space align="center">
                  <span style="width: 180px;">{{ discipline }}:</span>
                  <n-input-number
                    :value="getDisciplineOverride(discipline)"
                    @update:value="(val) => setDisciplineOverride(discipline, val)"
                    :min="0"
                    :max="1000"
                    :placeholder="`Default: ${defaultTopCut}`"
                    clearable
                    style="width: 150px;"
                  />
                  <n-button
                    v-if="getDisciplineOverride(discipline) !== undefined"
                    text
                    @click="removeDisciplineOverride(discipline)"
                    type="error"
                  >
                    Remove
                  </n-button>
                </n-space>
              </div>
            </n-space>
          </div>

          <!-- Validation Errors -->
          <n-alert
            v-if="validationErrors.length > 0"
            type="error"
            title="Configuration Errors"
            style="margin-bottom: 12px;"
          >
            <ul style="margin: 0; padding-left: 20px;">
              <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
            </ul>
          </n-alert>

          <!-- Configuration Summary -->
          <n-alert
            v-if="isValid"
            type="info"
            style="margin-bottom: 12px;"
          >
            <strong>Configuration:</strong> {{ configSummary }}
          </n-alert>

          <!-- Action Buttons -->
          <n-space>
            <div class="primaryButton">
              <n-button
                @click="handlePreview"
                :loading="isLoadingPreview"
                :disabled="!isValid"
                secondary
              >
                Preview Advancement
              </n-button>
            </div>
            <div class="primaryButton">
              <n-button
                @click="handleChoice(false)"
                :disabled="!isValid"
              >
                Advance to Next Round
              </n-button>
            </div>
          </n-space>
        </div>
      </div>
    </n-modal>
  </div>
  <div class="primaryModal">
    <n-modal
      :class="[isMobileView() ? 'popoverModalMobile' : 'popoverModal']"
      v-model:show="showAddProjectModal"
      preset="card"
      :title="'Add Projects to Round ' + (activeEvent.currentRound + 1)"
    >
      <div className="removeProjModalBody">
        <n-select
          placeholder="Select Project(s)"
          v-model:value="newProjects"
          :options="getAvailableProjects"
          multiple
          filterable
        >
          <template #empty> There are no projects to add. </template>
        </n-select>
      </div>
      <div className="primaryButton">
        <n-button @click="updateProjects()"> Add Project(s) </n-button>
      </div>
    </n-modal>
  </div>
  <div class="primaryModal">
    <n-modal
      :class="[isMobileView() ? 'popoverModalMobile' : 'popoverModal']"
      v-model:show="showPreviewModal"
      preset="card"
      title="Advancement Preview"
      style="width: 800px; max-width: 90vw;"
    >
      <div v-if="previewError">
        <n-alert type="error" title="Preview Error">
          {{ previewError }}
        </n-alert>
      </div>
      <div v-else-if="previewData">
        <n-space vertical>
          <!-- Summary Stats -->
          <n-alert type="info">
            <strong>Configuration:</strong> {{ previewData.configurationSummary }}<br/>
            <strong>Current Round:</strong> {{ previewData.currentRound }}<br/>
            <strong>Final Round:</strong> {{ previewData.isFinalRound ? 'Yes' : 'No' }}<br/>
            <strong>Total Advancing:</strong> {{ previewData.totalAdvancing }}<br/>
            <strong>Total Skipped:</strong> {{ previewData.totalSkipped }}
          </n-alert>

          <!-- Per-Discipline Stats -->
          <n-divider>Discipline Statistics</n-divider>
          <n-space vertical>
            <div v-for="(stats, discipline) in previewData.disciplineStats" :key="discipline">
              <n-card size="small" :title="discipline">
                <n-space>
                  <n-tag type="info">TopCut: {{ stats.topCut }}</n-tag>
                  <n-tag type="success">Advancing: {{ stats.advancing }}/{{ stats.totalProjects }}</n-tag>
                  <n-tag type="warning">Skipped: {{ stats.skipped }}</n-tag>
                  <n-tag>Rate: {{ stats.advancementRate }}</n-tag>
                </n-space>
              </n-card>
            </div>
          </n-space>

          <!-- Advancing Projects by Discipline -->
          <n-divider>Advancing Projects</n-divider>
          <n-collapse>
            <n-collapse-item v-for="(projects, discipline) in previewData.advancingByDiscipline" :key="discipline" :title="`${discipline} (${projects.length} advancing)`">
              <n-list bordered>
                <n-list-item v-for="project in projects" :key="project.projectId">
                  <div>
                    <div style="margin-bottom: 4px;">
                      <strong>{{ project.projectName }}</strong>
                    </div>
                    <div style="color: #666; font-size: 0.9em;">
                      Rank: {{ project.discRanking }}, Score: {{ project.avgScore?.toFixed(2) }}
                    </div>
                  </div>
                </n-list-item>
              </n-list>
            </n-collapse-item>
          </n-collapse>

          <!-- Skipped Projects (if any) -->
          <div v-if="previewData.skippedProjects.length > 0">
            <n-divider>Skipped Projects</n-divider>
            <n-collapse>
              <n-collapse-item :title="`${previewData.skippedProjects.length} projects will not advance`">
                <n-list bordered>
                  <n-list-item v-for="project in previewData.skippedProjects" :key="project.projectId">
                    <div>
                      <div style="margin-bottom: 4px;">
                        <strong>{{ project.projectName }}</strong>
                        <span style="color: #666;">({{ project.primaryDiscipline }})</span>
                      </div>
                      <div>
                        <n-tag type="error" size="small">{{ project.reason }}</n-tag>
                      </div>
                    </div>
                  </n-list-item>
                </n-list>
              </n-collapse-item>
            </n-collapse>
          </div>
        </n-space>
      </div>
      <template #footer>
        <n-button @click="showPreviewModal = false">Close</n-button>
      </template>
    </n-modal>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useMessage } from "naive-ui";
import DateFormatters from "@/helpers/DateFormatters.js";
import { useWindowSize } from "@/composables/useWindowSize";
import { useAdvancementConfig } from "@/composables/useAdvancementConfig";

export default defineComponent({
  computed: {
    getTime() {
      return this.activeEvent.dayOngoing && this.activeEvent.dayTimestamp
        ? this.time
        : "TBA";
    },
  },
  methods: {
    isMobileView() {
      if (this.windowWidth <= 650) {
        return true;
      } else {
        return false;
      }
    },
  },
  data() {
    const { width, height } = useWindowSize();
    return {
      showModal: ref(false),
      windowWidth: width,
      windowHeight: height,
    };
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const message = useMessage();

    const activeEvent = computed(() => {
      return store.getters.getEventsWithStatus("Active")[0];
    });

    // Initialize advancement configuration composable
    const {
      mode: advancementMode,
      defaultTopCut,
      disciplineOverrides,
      previewData,
      isLoadingPreview,
      previewError,
      validationErrors,
      isSimpleMode,
      isAdvancedMode,
      configPayload,
      configSummary,
      isValid,
      switchToSimpleMode,
      switchToAdvancedMode,
      setDisciplineOverride: composableSetDisciplineOverride,
      removeDisciplineOverride: composableRemoveDisciplineOverride,
      fetchPreview
    } = useAdvancementConfig(computed(() => activeEvent.value?.disciplines || []));

    const lastDay = ref(
      activeEvent.value?.timespan
        ? DateFormatters.getLength(activeEvent.value.timespan) ==
            activeEvent.value.currentDay
        : false
    );

    const time = ref(
      activeEvent.value?.dayTimestamp
        ? DateFormatters.getTimeElapsedFromString(activeEvent.value.dayTimestamp)
        : "TBA"
    );

    setInterval(() => {
      if (activeEvent.value?.dayTimestamp) {
        time.value = DateFormatters.getTimeElapsedFromString(
          activeEvent.value.dayTimestamp
        );
      }
    }, 1000);

    const ballots = computed(() => {
      return store.getters.getAllBallots;
    });

    const ballotsHaveBeenAllocated = computed(() => {
      return ballots.value && ballots.value.length > 0;
    });

    const showEndDayModal = ref(false);

    const isLoading = ref(false);
    const buttonText = computed(() => {
      const dayOngoing = activeEvent.value.dayOngoing;

      if (!ballotsHaveBeenAllocated.value) {
        return "Start Round";
      } else {
        if (dayOngoing) {
          return activeEvent.value.currentDay + 1 == numDays.value
            ? "Tabulate Results"
            : "End Day";
        } else {
          return "Allow Judging";
        }
      }
    });

    const numDays = computed(() => {
      return DateFormatters.getLength(activeEvent.value?.timespan);
    });

    const handleButton = () => {
      const dayOngoing = activeEvent.value.dayOngoing;

      if (!ballotsHaveBeenAllocated.value) {
        // Start the day
        isLoading.value = true;
        store
          .dispatch("startCurrentRound")
          .then(() => store.dispatch("fetchAllBallots"))
          .finally(() => (isLoading.value = false));
      } else {
        if (dayOngoing) {
          // End the day
          if (activeEvent.value.currentDay + 1 == numDays.value)
            store
              .dispatch("APIHelper", {
                name: "endDayNextRound",
                params: 1,
              })
              .then(() => {
                router.push({
                  name: "MainAdminDashboard",
                });
              });
          else showEndDayModal.value = true;
        } else {
          isLoading.value = true;
          store
            .dispatch("startJudging")
            .finally(() => (isLoading.value = false));
        }
      }
    };

    const handleChoice = (sameRound) => {
      // Capture event name before dispatch, since the store state changes after tabulate
      const eventName = activeEvent.value?.name;
      store
        .dispatch("APIHelper", {
          name: sameRound ? "endDaySameRound" : "endDayNextRound",
          // Use the advancement config payload for new API, maintains backward compatibility
          params: configPayload.value,
        })
        .then(() => {
          router.push({
            name: "EventAdminDashboard",
            params: {
              eventName: eventName,
            },
          });
        })
        .catch((error) => {
          // Handle specific error codes
          if (error.response?.status === 409) {
            message.error("Advancement is already in progress. Please wait and try again.");
          } else if (error.response?.status === 400) {
            const errorMsg = error.response?.data?.description || "Invalid advancement configuration";
            message.error(errorMsg);
          } else {
            message.error("Failed to advance to next round. Please try again.");
          }
        });
    };

    // Preview modal state
    const showPreviewModal = ref(false);

    // Handle preview button click
    const handlePreview = async () => {
      try {
        await fetchPreview(activeEvent.value.id);
        showPreviewModal.value = true;
      } catch (error) {
        message.error("Failed to load preview. " + (error.response?.data?.description || error.message));
      }
    };

    // Helper methods for discipline overrides
    const getDisciplineOverride = (discipline) => {
      return disciplineOverrides[discipline];
    };

    const setDisciplineOverride = (discipline, value) => {
      if (value === null || value === undefined || value === "") {
        // Clear override if empty
        composableRemoveDisciplineOverride(discipline);
      } else {
        composableSetDisciplineOverride(discipline, value);
      }
    };

    // Watch mode changes
    watch(advancementMode, (newMode) => {
      if (newMode === 'simple') {
        switchToSimpleMode();
      } else {
        switchToAdvancedMode();
      }
    });

    // Initialize with simple mode and default value of 1
    defaultTopCut.value = 1;

    const numAdvancing = ref(0);

    const resultsFromPrevRound = computed(() => {
      if (!activeEvent.value) return null;

      const round = activeEvent.value.currentRound;

      if (round == 0) return null;

      const id = activeEvent.value.id;
      const results = store.getters.getResultsForEvent(id);
      if (!results) return null;
      const projects = results.rounds[round - 1]?.projects;

      let ret = {};

      projects?.forEach((p) => (ret[p.id] = p));

      return ret;
    });

    const getAvailableProjects = computed(() => {
      let ret = store.getters.getAllProjects;
      const currRoundProjects = store.getters
        .getAllProjectsOfRound(activeEvent.value.currentRound)
        ?.map((p) => p.id);

      ret = ret?.filter((p) => !currRoundProjects.includes(p.id));

      ret?.sort((a, b) => {
        const aDisc =
          resultsFromPrevRound.value[a.id]?.disciplines.length > 1
            ? "Interdisciplinary"
            : resultsFromPrevRound.value[a.id]?.disciplines[0];
        const bDisc =
          resultsFromPrevRound.value[b.id]?.disciplines.length > 1
            ? "Interdisciplinary"
            : resultsFromPrevRound.value[b.id]?.disciplines[0];

        if (aDisc != bDisc) {
          return aDisc < bDisc ? -1 : 1;
        } else {
          return a.discRanking - b.discRanking;
        }
      });

      return ret?.map((p) => ({
        value: p.id,
        label:
          p.name +
          " (#" +
          resultsFromPrevRound.value[p.id]?.discRanking +
          " in " +
          (resultsFromPrevRound.value[p.id]?.disciplines.length > 1
            ? "Interdisciplinary"
            : resultsFromPrevRound.value[p.id]?.disciplines[0]) +
          ")",
      }));
    });

    const newProjects = ref([]);
    const showAddProjectModal = ref(false);
    const updateProjectsLoading = ref(false);

    const updateProjects = async () => {
      showAddProjectModal.value = false;
      updateProjectsLoading.value = true;

      newProjects.value.forEach(async (id) => {
        await store
          .dispatch("advanceProject", id)
          .then(() =>
            message.success("Advanced " + resultsFromPrevRound.value[id]?.name)
          )
          .catch(() =>
            message.error(
              "Advance " + resultsFromPrevRound.value[id]?.name + " Failed"
            )
          );
      });

      newProjects.value = [];
      updateProjectsLoading.value = false;
    };

    return {
      ballotsHaveBeenAllocated,
      activeEvent,
      isLoading,
      showEndDayModal,
      buttonText,
      handleButton,
      handleChoice,
      numAdvancing,
      getAvailableProjects,
      updateProjectsLoading,
      updateProjects,
      showAddProjectModal,
      newProjects,
      time,
      lastDay,
      // Advancement configuration
      advancementMode,
      defaultTopCut,
      disciplineOverrides,
      isSimpleMode,
      isAdvancedMode,
      configSummary,
      isValid,
      validationErrors,
      getDisciplineOverride,
      setDisciplineOverride,
      removeDisciplineOverride: composableRemoveDisciplineOverride,
      // Preview functionality
      showPreviewModal,
      previewData,
      previewError,
      isLoadingPreview,
      handlePreview,
    };
  },
});
</script>
